import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Video, Compass, Home, KeyRound } from "lucide-react";
import { motion } from "framer-motion";
import { Reveal } from "@/components/common/Reveal";
import { Eyebrow } from "@/components/common/Section";
import { Ridgeline } from "@/components/common/Ridgeline";
import { EdmontonMap } from "@/components/relocating/EdmontonMap";
import { CommunityCardList } from "@/components/relocating/CommunityCardList";
import { TerrainExplorer } from "@/components/relocating/TerrainExplorer";
import { FirstTracksForm } from "@/components/relocating/FirstTracksForm";

/**
 * /relocating — Moving to Edmonton landing.
 * Renter-side page. Nav CTA and footer column order handled by
 * useAudienceMode() based on pathname.
 *
 * NOTE: Calgary will duplicate this structure later — factor a shared
 * <RelocatingPage city="calgary" /> component when that lands.
 */

const STEPS = [
  {
    n: "01",
    icon: Video,
    title: "Tell us how you live.",
    body:
      "A 20-minute video call: work location, household, budget, the life you want. Kids' schools? Hospital shifts? River valley runs? That's the brief.",
    image:
      "https://images.unsplash.com/photo-1587560699334-cc4ff634909a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  },
  {
    n: "02",
    icon: Compass,
    title: "We scout the terrain.",
    body:
      "A personalized shortlist of neighbourhoods and homes — with honest trade-offs, not sales copy.",
    image:
      "https://images.unsplash.com/photo-1524661135-423995f22d0b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  },
  {
    n: "03",
    icon: Home,
    title: "Tour from your couch.",
    body:
      "Live video showings of every home on your list. Ask anything, see everything, skip the flight.",
    image:
      "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  },
  {
    n: "04",
    icon: KeyRound,
    title: "Arrive to your keys.",
    body:
      "Approved, signed, and move-in ready before you land. First grocery run tips included.",
    image:
      "https://images.unsplash.com/photo-1502005229762-cf1b2da7c5d6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  },
];

export default function Relocating() {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mql = window.matchMedia("(max-width: 767px)");
    const on = () => setIsMobile(mql.matches);
    on();
    mql.addEventListener("change", on);
    return () => mql.removeEventListener("change", on);
  }, []);

  const scrollTo = (id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div data-testid="page-relocating">
      {/* ============ 1. HERO ============ */}
      <header
        className="grain relative overflow-hidden"
        style={{ backgroundColor: "#24382E" }}
        data-nav-theme="dark"
        data-testid="relocating-hero"
      >
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(60% 60% at 12% 22%, rgba(200,154,78,0.14), rgba(20,35,27,0) 60%), radial-gradient(50% 60% at 100% 100%, rgba(174,196,199,0.12), rgba(20,35,27,0) 60%)",
          }}
        />
        <div className="relative mx-auto max-w-[1440px] px-6 pb-24 pt-[140px] md:px-10 md:pb-32 md:pt-[172px]">
          <div className="grid grid-cols-1 items-end gap-10 md:grid-cols-12">
            <div className="md:col-span-8">
              <Reveal>
                <Eyebrow color="#C89A4E">Relocating · Edmonton</Eyebrow>
              </Reveal>
              <Reveal delay={0.05}>
                <h1
                  className="font-display mt-7 max-w-[16ch]"
                  style={{
                    fontSize: "clamp(52px, 7.8vw, 118px)",
                    color: "#F8F5EF",
                    lineHeight: 0.94,
                    letterSpacing: "-0.015em",
                  }}
                  data-testid="relocating-title"
                >
                  New city. Soft{" "}
                  <span className="italic" style={{ color: "#E3B268" }}>
                    landing.
                  </span>
                </h1>
              </Reveal>
              <Reveal delay={0.1}>
                <p
                  className="body-measure mt-8 text-[17px] md:text-[19px]"
                  style={{ color: "rgba(237,233,224,0.82)" }}
                  data-testid="relocating-subcopy"
                >
                  Moving to Edmonton and don&rsquo;t know Wîhkwêntôwin from
                  Windermere? We do. Tell us how you live — we&rsquo;ll scout
                  the right neighbourhood, tour homes with you on video, and
                  have your keys ready the day you arrive.
                </p>
              </Reveal>
              <Reveal delay={0.15}>
                <div className="mt-10 flex flex-wrap items-center gap-4">
                  <a
                    href="#firsttracks"
                    onClick={scrollTo("firsttracks")}
                    className="pill pill-gold text-[15px]"
                    data-testid="relocating-primary-cta"
                  >
                    Join First Tracks — get listings early
                  </a>
                  <a
                    href="#terrain"
                    onClick={scrollTo("terrain")}
                    className="group inline-flex items-center gap-2 text-[14px]"
                    style={{ color: "#F8F5EF" }}
                    data-testid="relocating-secondary-cta"
                  >
                    <span className="label-caps text-[12px]">
                      Explore neighbourhoods
                    </span>
                    <ArrowUpRight
                      size={16}
                      className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                    />
                  </a>
                </div>
              </Reveal>
            </div>

            <div className="md:col-span-4">
              <Reveal delay={0.2}>
                <div
                  className="rounded-[20px] border p-6"
                  style={{
                    borderColor: "rgba(174,196,199,0.28)",
                    backgroundColor: "rgba(174,196,199,0.06)",
                    backdropFilter: "blur(8px)",
                  }}
                  data-testid="relocating-scoutcard"
                >
                  <p
                    className="label-caps text-[10px]"
                    style={{ color: "#C89A4E" }}
                  >
                    ROAM Scout™
                  </p>
                  <p
                    className="font-display mt-4"
                    style={{
                      fontSize: "clamp(24px,2.6vw,32px)",
                      color: "#F8F5EF",
                      lineHeight: 1.1,
                    }}
                  >
                    Free concierge placement for people relocating to
                    Alberta.
                  </p>
                  <p
                    className="mt-4 text-[13px]"
                    style={{ color: "rgba(237,233,224,0.72)" }}
                  >
                    We&rsquo;ve helped move families, doctors, roughnecks and
                    remote workers home. It&rsquo;s the same service we&rsquo;d
                    want if we were coming here for the first time.
                  </p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </header>

      {/* ============ 2. ROAM SCOUT — HOW IT WORKS ============ */}
      <ScoutSection />

      {/* ============ 3. THE TERRAIN — SPLIT EXPLORER ============ */}

      {/* ============ 3. THE TERRAIN — SPLIT EXPLORER ============ */}
      <TerrainExplorer />

      {/* ============ 4. FIRST TRACKS WAITLIST ============ */}
      <section
        id="firsttracks"
        className="grain relative"
        style={{ backgroundColor: "#24382E" }}
        data-nav-theme="dark"
        data-testid="relocating-firsttracks"
      >
        <div className="relative z-10 mx-auto max-w-[1440px] px-6 py-24 md:px-10 md:py-32">
          <div className="grid grid-cols-1 items-end gap-10 md:grid-cols-12 md:gap-14">
            <div className="md:col-span-6">
              <Reveal>
                <Eyebrow color="#C89A4E">First Tracks · waitlist</Eyebrow>
              </Reveal>
              <Reveal delay={0.05}>
                <h2
                  className="font-display mt-6 max-w-[15ch]"
                  style={{
                    fontSize: "clamp(40px, 5.2vw, 76px)",
                    color: "#F8F5EF",
                    lineHeight: 1,
                    letterSpacing: "-0.015em",
                  }}
                >
                  See homes{" "}
                  <span className="italic" style={{ color: "#E3B268" }}>
                    before
                  </span>{" "}
                  everyone else.
                </h2>
              </Reveal>
              <Reveal delay={0.1}>
                <p
                  className="body-measure mt-6 text-[16px] md:text-[18px]"
                  style={{ color: "rgba(237,233,224,0.78)" }}
                >
                  First Tracks members get new ROAM listings 24–48 hours
                  before they hit the public platforms — plus honest
                  neighbourhood intel for your move.
                </p>
              </Reveal>
            </div>
            <div className="md:col-span-6">
              <Reveal delay={0.15}>
                <FirstTracksForm />
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ============ 5. CLOSING CTA ============ */}
      <section
        style={{ backgroundColor: "#EDE9E0" }}
        data-nav-theme="light"
        data-testid="relocating-closing"
      >
        <div className="mx-auto max-w-[1440px] px-6 py-20 md:px-10 md:py-28">
          <div className="flex flex-col items-start justify-between gap-8 md:flex-row md:items-end">
            <div className="max-w-[36ch]">
              <Reveal>
                <p
                  className="font-display"
                  style={{
                    fontSize: "clamp(28px, 3.6vw, 46px)",
                    color: "#201E1A",
                    lineHeight: 1.05,
                  }}
                >
                  Coming from further afield?
                </p>
              </Reveal>
              <Reveal delay={0.05}>
                <p
                  className="mt-4 text-[15px] leading-[1.6]"
                  style={{ color: "rgba(32,30,26,0.7)" }}
                >
                  We work with people relocating from every province and a
                  handful of countries. If you can send a video call, we can
                  scout your city.
                </p>
              </Reveal>
            </div>
            <Reveal delay={0.1}>
              <div className="flex flex-wrap items-center gap-4">
                <Link
                  to="/contact"
                  className="pill pill-pine text-[15px]"
                  data-testid="relocating-book-scout"
                >
                  Book a Scout call
                </Link>
                <Link
                  to="/residences"
                  className="group inline-flex items-center gap-2 text-[14px]"
                  style={{ color: "#24382E" }}
                >
                  <span className="label-caps text-[12px]">
                    Browse current homes
                  </span>
                  <ArrowUpRight size={16} />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
        <div className="w-full opacity-70" aria-hidden>
          <Ridgeline stroke="#C89A4E" height={90} />
        </div>
      </section>
    </div>
  );
}


/* ============================================================
 * ROAM Scout section — the "how it works" showcase.
 * Sticky visual column on the left that swaps images per active
 * step (driven by whichever step is closest to the viewport
 * centre). Warm gradient + grain background. Step cards on the
 * right have gold icon medallions, hover halos, and stagger-in.
 * ============================================================ */
const ScoutSection = () => {
  const [active, setActive] = useState(0);

  // Track which step card is nearest the vertical centre of the viewport
  useEffect(() => {
    const cards = STEPS.map((_, i) =>
      document.querySelector(`[data-testid="scout-step-${STEPS[i].n}"]`),
    );
    const onScroll = () => {
      const centre = window.innerHeight * 0.5;
      let best = 0;
      let bestDist = Infinity;
      cards.forEach((el, i) => {
        if (!el) return;
        const r = el.getBoundingClientRect();
        const d = Math.abs(r.top + r.height / 2 - centre);
        if (d < bestDist) {
          bestDist = d;
          best = i;
        }
      });
      setActive(best);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <section
      className="relative overflow-hidden"
      style={{ backgroundColor: "#F6EEDD" }}
      data-nav-theme="light"
      data-testid="relocating-scout"
    >
      {/* Layered background: warm gradient + subtle noise + gold radial glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(80% 60% at 8% 12%, rgba(200,154,78,0.22) 0%, rgba(246,238,221,0) 55%)," +
            "radial-gradient(60% 60% at 100% 100%, rgba(36,56,46,0.14) 0%, rgba(246,238,221,0) 60%)," +
            "linear-gradient(180deg, #F6EEDD 0%, #F1E7CF 100%)",
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.18] mix-blend-multiply grain"
      />

      <div className="relative mx-auto max-w-[1440px] px-6 py-24 md:px-10 md:py-32">
        <div className="grid grid-cols-1 gap-14 md:grid-cols-12 md:gap-16">
          {/* -------- LEFT — sticky visual column -------- */}
          <div className="md:col-span-5">
            <div className="md:sticky md:top-24">
              <Reveal>
                <Eyebrow>ROAM Scout&trade; &middot; Concierge placement &mdash; free</Eyebrow>
              </Reveal>
              <Reveal delay={0.04}>
                <h2
                  className="font-display mt-6"
                  style={{
                    fontSize: "clamp(40px, 5.2vw, 76px)",
                    color: "#201E1A",
                    lineHeight: 1,
                    letterSpacing: "-0.015em",
                  }}
                >
                  We scout. You arrive{" "}
                  <span className="italic" style={{ color: "#C89A4E" }}>
                    home.
                  </span>
                </h2>
              </Reveal>

              {/* Cinematic image card that swaps per active step */}
              <Reveal delay={0.1} className="mt-10">
                <div
                  className="relative overflow-hidden"
                  style={{
                    borderRadius: 22,
                    aspectRatio: "4 / 5",
                    boxShadow: "0 40px 80px -30px rgba(20,35,27,0.5)",
                  }}
                  data-testid="scout-visual"
                >
                  {STEPS.map((s, i) => (
                    <motion.img
                      key={s.n}
                      src={s.image}
                      alt={s.title}
                      className="absolute inset-0 h-full w-full object-cover"
                      initial={false}
                      animate={{
                        opacity: active === i ? 1 : 0,
                        scale: active === i ? 1 : 1.04,
                      }}
                      transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
                    />
                  ))}
                  <div
                    className="pointer-events-none absolute inset-0"
                    style={{
                      background:
                        "linear-gradient(180deg, rgba(20,35,27,0) 45%, rgba(20,35,27,0.55) 100%)",
                    }}
                  />
                  {/* Active step badge over the image */}
                  <motion.div
                    key={`badge-${active}`}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                    className="absolute bottom-5 left-5 right-5 flex items-end justify-between gap-4"
                  >
                    <div>
                      <p
                        className="label-caps text-[10px]"
                        style={{ color: "#E3B268" }}
                      >
                        Step {STEPS[active].n} &middot; live
                      </p>
                      <p
                        className="font-display mt-1"
                        style={{
                          fontSize: 22,
                          color: "#F8F5EF",
                          lineHeight: 1.1,
                          textShadow: "0 2px 12px rgba(20,35,27,0.5)",
                        }}
                      >
                        {STEPS[active].title}
                      </p>
                    </div>
                    <div
                      className="flex items-center gap-1.5 rounded-full px-2.5 py-1"
                      style={{
                        backgroundColor: "rgba(248,245,239,0.9)",
                        color: "#24382E",
                      }}
                    >
                      <span
                        className="h-1.5 w-1.5 rounded-full"
                        style={{ backgroundColor: "#C89A4E" }}
                      />
                      <span className="label-caps text-[9px]">Free service</span>
                    </div>
                  </motion.div>
                </div>
              </Reveal>

              {/* Little trust chips under the visual */}
              <Reveal delay={0.15}>
                <ul className="mt-6 flex flex-wrap gap-2">
                  {[
                    "No fees, ever",
                    "Local scouts",
                    "Video-first",
                    "Answer within a day",
                  ].map((t) => (
                    <li
                      key={t}
                      className="label-caps rounded-full px-3 py-1.5 text-[10px]"
                      style={{
                        backgroundColor: "rgba(248,245,239,0.75)",
                        color: "#24382E",
                        border: "1px solid rgba(32,30,26,0.1)",
                      }}
                    >
                      {t}
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>
          </div>

          {/* -------- RIGHT — step cards -------- */}
          <ol
            className="md:col-span-7 md:pl-4"
            data-testid="relocating-steps"
          >
            {STEPS.map((s, i) => {
              const isActive = active === i;
              const Icon = s.icon;
              return (
                <motion.li
                  key={s.n}
                  data-testid={`scout-step-${s.n}`}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{
                    duration: 0.9,
                    delay: i * 0.05,
                    ease: [0.16, 1, 0.3, 1],
                  }}
                  className="relative"
                >
                  <div
                    className="relative overflow-hidden p-6 transition-all duration-500 md:p-8"
                    style={{
                      borderRadius: 20,
                      marginTop: i === 0 ? 0 : 20,
                      backgroundColor: isActive
                        ? "rgba(248,245,239,0.92)"
                        : "rgba(248,245,239,0.55)",
                      border: `1px solid ${
                        isActive
                          ? "rgba(200,154,78,0.55)"
                          : "rgba(32,30,26,0.08)"
                      }`,
                      boxShadow: isActive
                        ? "0 30px 60px -30px rgba(20,35,27,0.45)"
                        : "0 6px 16px -10px rgba(20,35,27,0.18)",
                      transform: isActive
                        ? "translateY(-2px)"
                        : "translateY(0)",
                    }}
                  >
                    {/* subtle gold halo on active */}
                    {isActive && (
                      <span
                        aria-hidden
                        className="pointer-events-none absolute -inset-1"
                        style={{
                          background:
                            "radial-gradient(50% 60% at 0% 0%, rgba(200,154,78,0.28), rgba(200,154,78,0) 60%)",
                        }}
                      />
                    )}

                    <div className="relative grid grid-cols-[auto,1fr] gap-5 md:gap-7">
                      {/* Number + icon medallion */}
                      <div className="flex flex-col items-center gap-3">
                        <span
                          className="grid h-14 w-14 place-items-center rounded-full transition-all duration-500"
                          style={{
                            backgroundColor: isActive
                              ? "#24382E"
                              : "rgba(36,56,46,0.08)",
                            color: isActive ? "#E3B268" : "#24382E",
                          }}
                          aria-hidden
                        >
                          <Icon size={22} strokeWidth={1.6} />
                        </span>
                        <span
                          className="font-display leading-none"
                          style={{
                            fontSize: "clamp(28px, 3vw, 40px)",
                            color: isActive ? "#C89A4E" : "rgba(32,30,26,0.35)",
                            letterSpacing: "-0.02em",
                            transition: "color 0.5s",
                          }}
                        >
                          {s.n}
                        </span>
                      </div>

                      <div>
                        <h3
                          className="font-display"
                          style={{
                            fontSize: "clamp(24px, 2.6vw, 34px)",
                            color: "#201E1A",
                            lineHeight: 1.1,
                          }}
                        >
                          {s.title}
                        </h3>
                        <p
                          className="mt-3 text-[16px] leading-[1.65] md:text-[17px]"
                          style={{ color: "rgba(32,30,26,0.78)" }}
                        >
                          {s.body}
                        </p>
                        {/* connector between cards */}
                        {i < STEPS.length - 1 && (
                          <span
                            aria-hidden
                            className="absolute left-[41px] -bottom-5 block h-5 w-[2px] md:left-[57px]"
                            style={{
                              background: isActive
                                ? "linear-gradient(180deg, #C89A4E, rgba(200,154,78,0))"
                                : "linear-gradient(180deg, rgba(32,30,26,0.15), rgba(32,30,26,0))",
                            }}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </motion.li>
              );
            })}
          </ol>
        </div>
      </div>
    </section>
  );
};
